import dayjs, { type Dayjs } from "dayjs"

import { BookingTypeEnum, type Contract, type Child, type Booking } from "@/graphql/types"
import { i18n } from "@/i18n"

import { calculateAge, monthLabels } from "./calendar"
import { getCoveredMonthsInYearRange, hasOverlapOrInside } from "./date"
import { WEIGHTING_FACTORS, type WeightingKey } from "./weighting"

export type BookingWithWeighting = {
  validMonthsOfContract: number[]
  validMonthsOfBooking: number[]
  from: string
  to: string
  regularChild: boolean
  nurseryChildOrUnder3: boolean
  migration: boolean
  assistedIntegration: boolean
  schoolChild: boolean
}

export const WEIGHTING_LABEL_KEYS: Record<WeightingKey, string> = {
  regularChild: "dashboard.weighting.standard_children",
  schoolChild: "dashboard.weighting.pupils",
  migration: "dashboard.weighting.migration",
  nurseryChildOrUnder3: "dashboard.weighting.nursery_children",
  assistedIntegration: "dashboard.weighting.integration",
}

const chartConfig = {
  regularChild: {
    label: i18n.global.t(WEIGHTING_LABEL_KEYS.regularChild),
    backgroundColor: "royalblue",
  },
  schoolChild: {
    label: i18n.global.t(WEIGHTING_LABEL_KEYS.schoolChild),
    backgroundColor: "lightskyblue",
  },
  migration: {
    label: i18n.global.t(WEIGHTING_LABEL_KEYS.migration),
    backgroundColor: "powderblue",
  },
  nurseryChildOrUnder3: {
    label: i18n.global.t(WEIGHTING_LABEL_KEYS.nurseryChildOrUnder3),
    backgroundColor: "turquoise",
  },
  assistedIntegration: {
    label: i18n.global.t(WEIGHTING_LABEL_KEYS.assistedIntegration),
    backgroundColor: "lightgreen",
  },
}

function getActiveStandardBookings(items: Child[]): BookingWithWeighting[] {
  const firstDayOfCurrentYear = dayjs().startOf("year")
  const lastDayOfCurrentYear = dayjs().endOf("year")

  return (items ?? []).flatMap((child) => {
    if (!child.contracts) return []

    const validContracts =
      child?.contracts?.filter(({ startOfContract, endOfContract }) =>
        hasOverlapOrInside(
          startOfContract,
          endOfContract ?? lastDayOfCurrentYear,
          firstDayOfCurrentYear,
          lastDayOfCurrentYear
        )
      ) ?? []

    const validMonthsOfContracts = getValidMonths(
      validContracts,
      lastDayOfCurrentYear,
      "startOfContract",
      "endOfContract"
    )

    const activeStandardBookings = validContracts.flatMap(
      ({ id: contractId, endOfContract, bookings }) => {
        const validBookings = bookings?.filter(
          (booking) =>
            booking?.type === BookingTypeEnum.Standard &&
            hasOverlapOrInside(
              booking.from,
              booking.to ?? endOfContract ?? lastDayOfCurrentYear,
              firstDayOfCurrentYear,
              lastDayOfCurrentYear
            )
        )
        const validMonthsOfBookings = getValidMonths(
          validBookings,
          lastDayOfCurrentYear,
          "from",
          "to"
        )

        const mappedBookings = validBookings.map((booking) => {
          const {
            id: bookingId,
            from,
            to,
            nurseryChild,
            migration,
            assistedIntegration,
            isSchoolChild: schoolChild,
          } = booking

          return {
            validMonthsOfContract: validMonthsOfContracts?.[contractId]?.months ?? [],
            validMonthsOfBooking: validMonthsOfBookings?.[bookingId]?.months ?? [],
            from: dayjs(from).isBefore(firstDayOfCurrentYear) ? firstDayOfCurrentYear : from,
            to:
              to && dayjs(to).isAfter(lastDayOfCurrentYear)
                ? lastDayOfCurrentYear
                : !to
                  ? endOfContract
                    ? dayjs(endOfContract).isAfter(lastDayOfCurrentYear)
                      ? lastDayOfCurrentYear
                      : endOfContract
                    : lastDayOfCurrentYear
                  : to,
            ...resolveWeightings({
              childBirthDate: child.birth?.date,
              bookingFrom: from,
              nurseryChild,
              migration,
              assistedIntegration,
              schoolChild,
            }),
          }
        })
        return mappedBookings
      }
    )
    return activeStandardBookings
  })
}

function getDataForSets(activeStandardBookings: BookingWithWeighting[]) {
  const dataForSets: Record<string, number[]> = {
    regularChild: Array(12).fill(0),
    nurseryChildOrUnder3: Array(12).fill(0),
    migration: Array(12).fill(0),
    assistedIntegration: Array(12).fill(0),
    schoolChild: Array(12).fill(0),
  }

  activeStandardBookings.forEach((booking: BookingWithWeighting) => {
    if (!booking) return

    const {
      regularChild,
      nurseryChildOrUnder3,
      migration,
      assistedIntegration,
      schoolChild,
      validMonthsOfContract,
      validMonthsOfBooking,
    } = booking

    const weightings: Record<WeightingKey, boolean> = {
      regularChild,
      nurseryChildOrUnder3,
      migration,
      assistedIntegration,
      schoolChild,
    }

    validMonthsOfBooking.forEach((month) => {
      const trueWeighting = getWeightingKey(weightings)
      if (trueWeighting && validMonthsOfContract.includes(month))
        dataForSets[trueWeighting][month] += 1
    })
  })
  return dataForSets
}

export function mapBookingsToChartData(items: Child[]) {
  const activeStandardBookings = getActiveStandardBookings(items)
  const dataForSets = getDataForSets(activeStandardBookings)

  const chartData = {
    labels: monthLabels,
    datasets: Object.entries(chartConfig).map(([key, { label, backgroundColor }]) => ({
      type: "bar",
      label,
      backgroundColor,
      data: dataForSets[key] || [],
    })),
  }

  return chartData
}

type MonthCoverage = Record<string, { months: number[]; start: string; end: string }>

export function getValidMonths<
  T extends Booking | Contract,
  KStart extends keyof T,
  KEnd extends keyof T,
>(items: T[], lastDayOfCurrentYear: Dayjs, startKey: KStart, endKey: KEnd): MonthCoverage {
  const monthMap: MonthCoverage = {}

  items.forEach((record) => {
    const id = record.id
    const start = record[startKey] as string
    const end = (record[endKey] as string | undefined) ?? lastDayOfCurrentYear.toISOString()

    const months = getCoveredMonthsInYearRange(start, end, dayjs().year())
    monthMap[id] = { months, start, end }
  })

  for (let month = 0; month < 12; month++) {
    const overlapping = Object.entries(monthMap).filter(([, data]) => data.months.includes(month))
    if (overlapping.length <= 1) continue

    const lastDayOfMonth = dayjs().month(month).endOf("month")

    /* The count for the same child for one month will always be maximum 1
    when there are multiple contracts,bookings for one child for one month the one which has the
    last day of the month or the latest one should be respected */
    const chosen =
      overlapping.find(
        ([, data]) =>
          dayjs(data.start).isSameOrBefore(lastDayOfMonth, "day") &&
          dayjs(data.end).isSameOrAfter(lastDayOfMonth, "day")
      ) ??
      overlapping.reduce((latest, current) =>
        dayjs(current[1].end).isAfter(dayjs(latest[1].end)) ? current : latest
      )

    const respectedId = chosen[0]
    for (const [id, data] of overlapping) {
      if (id !== respectedId) {
        data.months = data.months.filter((m) => m !== month)
      }
    }
  }

  return monthMap
}

export type WeightingInput = {
  childBirthDate?: string
  bookingFrom?: string
  nurseryChild: boolean
  migration: boolean
  assistedIntegration: boolean
  schoolChild: boolean
  monthEnd?: Dayjs
}

export function getWeightingKey(weightings: Record<WeightingKey, boolean>): WeightingKey {
  return (Object.entries(weightings) as [WeightingKey, boolean][])
    .filter(([_, value]) => value)
    .map(([key]) => key)[0]
}

export function resolveWeightings({
  childBirthDate,
  nurseryChild,
  migration,
  assistedIntegration,
  schoolChild,
  monthEnd,
}: WeightingInput): { [K in WeightingKey]: boolean } {
  const referenceDate = monthEnd ?? dayjs()
  const childAgeUnder3 = childBirthDate
    ? calculateAge(dayjs(childBirthDate), referenceDate) < 3
    : false

  const nurseryChildOrUnder3 = nurseryChild || childAgeUnder3
  const regularChild = !nurseryChildOrUnder3 && !migration && !assistedIntegration && !schoolChild

  const rawWeightings: Record<WeightingKey, boolean> = {
    regularChild,
    nurseryChildOrUnder3,
    migration,
    assistedIntegration,
    schoolChild,
  }

  if (regularChild) return rawWeightings

  const trueWeightings = (Object.entries(rawWeightings) as [WeightingKey, boolean][])
    .filter(([_, value]) => value)
    .map(([key]) => key)

  const weightingWHighestFactor = trueWeightings.reduce((highest, current) =>
    WEIGHTING_FACTORS[current] > WEIGHTING_FACTORS[highest] ? current : highest
  )

  return {
    regularChild: false,
    nurseryChildOrUnder3: weightingWHighestFactor === "nurseryChildOrUnder3",
    migration: weightingWHighestFactor === "migration",
    assistedIntegration: weightingWHighestFactor === "assistedIntegration",
    schoolChild: weightingWHighestFactor === "schoolChild",
  }
}

export function getBookingWeightingKey(booking: Booking): WeightingKey {
  const weightings = resolveWeightings({
    childBirthDate: booking.contract.child.birth?.date,
    bookingFrom: booking.from,
    nurseryChild: booking.nurseryChild,
    migration: booking.migration,
    assistedIntegration: booking.assistedIntegration,
    schoolChild: booking.isSchoolChild,
  })
  return getWeightingKey(weightings)
}

export function getBookingWeightingLabel(booking: Booking): string {
  const weightingKey = getBookingWeightingKey(booking)
  return i18n.global.t(WEIGHTING_LABEL_KEYS[weightingKey])
}
